import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  TrendingUp,
  Users,
  Stethoscope,
  Building2 as HospitalIcon,
  Activity,
  ArrowUpRight,
  Calendar,
  Clock,
  Layers,
  Plus,
  Play,
  Square,
  ChevronRight,
  MoreVertical,
  ShieldCheck,
  CreditCard,
  Target,
  ArrowRightCircle,
  FileText,
  AlertCircle,
  Settings,
  HelpCircle
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie
} from 'recharts';

import DashboardHeader from '../../Components/common/DashboardHeader';
import { useAuth } from '../../context/AuthContext';
import Skeleton from '../../Components/common/Skeleton';

const AdminDashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/dashboard/admin');
      setData(response.data.data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to load analytical engine');
    } finally {
      setLoading(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="space-y-8 pb-20">
        <DashboardHeader user={user} />


        {/* Metric Cards Skeleton */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {Array(4).fill(0).map((_, i) => (
            <div key={i} className="bg-white p-5 sm:p-6 lg:p-8 rounded-[24px] sm:rounded-[32px] border border-slate-100 shadow-sm space-y-4">
              <div className="flex justify-between items-start">
                <Skeleton className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl" />
                <Skeleton className="h-5 w-12 rounded-lg" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-3 w-20 rounded-lg" />
                <Skeleton className="h-8 w-24 rounded-xl" />
                <Skeleton className="h-3 w-32 rounded-lg" />
              </div>
            </div>
          ))}
        </div>

        {/* Charts & Revenue Skeletons */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8">
          <div className="lg:col-span-7 xl:col-span-8 bg-white rounded-[24px] sm:rounded-[36px] p-5 sm:p-8 border border-slate-100 shadow-sm space-y-6">
            <div className="space-y-2">
              <Skeleton className="h-6 w-48 rounded-xl" />
              <Skeleton className="h-3 w-72 rounded-lg" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Skeleton className="h-[250px] w-full rounded-2xl animate-pulse" />
              <Skeleton className="h-[250px] w-full rounded-2xl animate-pulse" />
            </div>
          </div>
          <div className="lg:col-span-5 xl:col-span-4 bg-white rounded-[24px] sm:rounded-[36px] p-5 sm:p-8 border border-slate-100 shadow-sm flex flex-col justify-between min-h-[300px]">
            <div className="space-y-4 w-full">
              <Skeleton className="w-12 h-12 rounded-2xl animate-pulse" />
              <Skeleton className="h-3 w-24 rounded-lg" />
              <Skeleton className="h-8 w-44 rounded-xl animate-pulse" />
              <Skeleton className="h-14 w-full rounded-2xl animate-pulse" />
              <Skeleton className="h-14 w-full rounded-2xl animate-pulse" />
            </div>
          </div>
        </div>

        {/* Conversion & Activity Skeletons */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8">
          <div className="lg:col-span-5 xl:col-span-5 bg-white rounded-[24px] sm:rounded-[36px] p-5 sm:p-8 border border-slate-100 shadow-sm flex flex-col items-center justify-between">
            <div className="self-start w-full">
              <Skeleton className="h-6 w-36 rounded-xl animate-pulse" />
            </div>
            <Skeleton className="w-48 h-48 rounded-full my-6 animate-pulse" />
            <div className="grid grid-cols-3 gap-4 w-full">
              <Skeleton className="h-10 w-full rounded-lg animate-pulse" />
              <Skeleton className="h-10 w-full rounded-lg animate-pulse" />
              <Skeleton className="h-10 w-full rounded-lg animate-pulse" />
            </div>
          </div>
          <div className="lg:col-span-7 xl:col-span-7 bg-white rounded-[24px] sm:rounded-[36px] p-5 sm:p-8 border border-slate-100 shadow-sm space-y-6">
            <div className="space-y-2">
              <Skeleton className="h-6 w-48 rounded-xl animate-pulse" />
              <Skeleton className="h-3 w-64 rounded-lg animate-pulse" />
            </div>
            <div className="space-y-4">
              {Array(3).fill(0).map((_, i) => (
                <div key={i} className="flex items-center justify-between py-3 border-b border-slate-50">
                  <div className="flex items-center gap-3">
                    <Skeleton className="w-12 h-12 rounded-2xl animate-pulse" />
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-32 rounded-lg animate-pulse" />
                      <Skeleton className="h-3 w-24 rounded-lg animate-pulse" />
                    </div>
                  </div>
                  <Skeleton className="w-8 h-8 rounded-lg animate-pulse" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const {
    counts = {},
    financials = {},
    leadStats = {},
    surgeryStats = {},
    recentLeads = [],
    recentPayments = [],
    leadSourceStats = {},
    staleLeads = [],
    onlineUsers = [],
    agentStats = [],
    pendingRequestFollowUps = [],
    recentlyLostLeads = []
  } = data || {};

  // Simplified Analytics Data
  const barData = Object.entries(leadStats).map(([key, value]) => ({
    name: key.replace(/_/g, ' ').toUpperCase(),
    shortName: key.split('_')[0].toUpperCase(),
    value: value,
  })).filter(d => d.value > 0).slice(0, 6);

  const sourceData = leadSourceStats && Object.keys(leadSourceStats).length > 0
    ? Object.entries(leadSourceStats).map(([key, value]) => ({
      name: key === 'DOXEZ' ? 'Website' : (key === 'unknown' ? 'Not Specified' : key.replace(/_/g, ' ').toUpperCase()),
      value: value,
    })).filter(d => d.value > 0)
    : [];

  const gaugeData = [
    { name: 'Success', value: counts.totalLeads > 0 ? ((leadStats.completed || 0) / counts.totalLeads) * 100 : 0, fill: '#10B981' },
    { name: 'Remaining', value: 100 - (counts.totalLeads > 0 ? ((leadStats.completed || 0) / counts.totalLeads) * 100 : 0), fill: '#F1F5F9' },
  ];

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-1000 pb-20">

      <DashboardHeader user={user} />

      {/* ⚠️ STALE LEADS WARNING BANNER
      {staleLeads.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-[2rem] p-6 shadow-sm mb-6 animate-in fade-in slide-in-from-top-4 duration-500">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-2xl flex items-center justify-center text-amber-600 shrink-0">
                <AlertCircle className="w-6 h-6 animate-bounce" />
              </div>
              <div>
                <h3 className="text-sm font-black text-amber-950 uppercase tracking-wider">URGENT: LEADS WAITING TOO LONG (OVER 10 MINS)</h3>
                <p className="text-xs text-amber-800/80 font-bold mt-0.5">The following new leads have been waiting for more than 10 minutes. Please take immediate action to assign or process them.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-2">
              {staleLeads.map(lead => {
                const diffMins = Math.round((new Date() - new Date(lead.createdAt)) / 60000);
                const timeStr = diffMins < 60
                  ? `${diffMins} mins`
                  : `${Math.floor(diffMins / 60)} hr ${diffMins % 60} mins`;
                const ccName = lead.assignedCustomerCare
                  ? `${lead.assignedCustomerCare.firstName} ${lead.assignedCustomerCare.lastName}`
                  : 'Unassigned';

                return (
                  <div key={lead._id} className="p-4 bg-white rounded-2xl border border-amber-100 flex justify-between items-center hover:shadow-md transition-all">
                    <div className="min-w-0 flex-1 pr-2">
                      <p className="text-xs font-black text-slate-900 truncate">{lead.patientName}</p>
                      <p className="text-[10px] text-slate-400 font-semibold truncate mt-0.5">{lead.patientPhone}</p>
                      <p className="text-[10px] text-slate-500 font-bold mt-1 truncate">
                        CC: <span className="text-slate-700 font-black">{ccName}</span>
                      </p>
                      <p className="text-[9px] text-amber-600 font-black mt-1 uppercase tracking-wider">
                        Pending: {timeStr}
                      </p>
                    </div>
                    <button
                      onClick={() => navigate(`/leads/${lead._id}`)}
                      className="p-2 bg-amber-600 text-white rounded-xl shadow hover:bg-amber-700 transition-colors shrink-0"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      */}

      {/* 📊 INSTANT INTELLIGENCE (Easy Understanding) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <MetricCard
          label="Total Patients"
          value={counts.totalLeads}
          subLabel="Registered in System"
          icon={Users}
          color="indigo"
          trend="+12%"
        />
        <MetricCard
          label="Surgical Cases"
          value={surgeryStats.in_progress || 0}
          subLabel="Active Operations"
          icon={Activity}
          color="emerald"
          trend="Live"
        />
        <MetricCard
          label="Available Doctors"
          value={counts.totalDoctors}
          subLabel="Verified Clinical Partners"
          icon={Stethoscope}
          color="blue"
          trend="Active"
        />
        <MetricCard
          label="System Health"
          value="99.9%"
          subLabel="Uptime Integrity"
          icon={ShieldCheck}
          color="amber"
          trend="Stable"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8">
        {/* 📈 WORKFLOW ANALYTICS */}
        <div className="lg:col-span-7 xl:col-span-8 bg-white rounded-[24px] sm:rounded-[36px] p-5 sm:p-6 lg:p-8 shadow-2xl shadow-gray-200/50 border border-gray-50 flex flex-col relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50 rounded-full -mr-32 -mt-32 blur-3xl opacity-50"></div>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 sm:mb-8 relative z-10 gap-4">
            <div>
              <h3 className="text-xl sm:text-2xl font-black text-gray-900 tracking-tighter">Case Distribution</h3>
              <p className="text-gray-400 font-bold uppercase text-[9px] sm:text-[10px] tracking-[0.2em] sm:tracking-[0.3em] mt-1">Where your leads are currently sitting</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-xl border border-gray-100">
                <Target className="w-3.5 h-3.5 text-[#143E78]" />
                <span className="text-[9px] sm:text-[10px] font-black text-[#143E78] uppercase tracking-widest">Real-time Data</span>
              </div>
            </div>
          </div>

          <div className="flex-1 grid grid-cols-1 2xl:grid-cols-2 gap-6 lg:gap-8 relative z-10">
            {/* Lead Status Bar Chart */}
            <div className="min-h-[300px]">
              <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-6">Status Breakdown</h4>
              <ResponsiveContainer width="100%" height={300} minWidth={1} minHeight={1}>
                <BarChart data={barData}>
                  <CartesianGrid strokeDasharray="8 8" vertical={false} stroke="#F1F5F9" />
                  <XAxis dataKey="shortName" axisLine={false} tickLine={false} tick={{ fill: '#94A3B8', fontSize: 10, fontWeight: 900 }} dy={15} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94A3B8', fontSize: 10, fontWeight: 900 }} />
                  <Tooltip cursor={{ fill: 'rgba(20, 62, 120, 0.05)', radius: 12 }} content={<AdvancedTooltip />} />
                  <Bar dataKey="value" radius={[10, 10, 10, 10]} barSize={40}>
                    {barData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={index % 2 === 0 ? '#143E78' : '#0F4C3F'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Lead Source Pie Chart */}
            <div className="min-h-[300px] flex flex-col">
              <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-6">Source Attribution</h4>
              <div className="flex-1 relative">
                {sourceData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={260} minWidth={1} minHeight={1}>
                    <PieChart>
                      <Pie
                        data={sourceData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                        nameKey="name"
                        labelLine={false}
                      >
                        {sourceData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={['#143E78', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'][index % 5]} />
                        ))}
                      </Pie>
                      <Tooltip content={<AdvancedTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[200px] flex flex-col items-center justify-center bg-gray-50 rounded-3xl border-2 border-dashed border-gray-100 p-8 text-center">
                    <Activity className="w-8 h-8 text-gray-200 mb-3" />
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">No Source Data Available</p>
                  </div>
                )}

                {sourceData.length > 0 && (
                  <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-4 px-4">
                    {sourceData.map((entry, index) => (
                      <div key={entry.name} className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: ['#143E78', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'][index % 5] }}></div>
                        <span className="text-[9px] font-black text-gray-500 uppercase tracking-tighter">{entry.name}: {entry.value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 💰 CASH FLOW CENTER */}
        <div className="lg:col-span-5 xl:col-span-4 space-y-6 sm:space-y-8">
          <div className="bg-[#143E78] rounded-[24px] sm:rounded-[36px] p-5 sm:p-6 lg:p-8 text-white shadow-[0_30px_60px_-15px_rgba(20,62,120,0.4)] relative overflow-hidden group min-h-[300px] flex flex-col justify-between">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-transparent"></div>
            <div className="relative z-10">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/10 backdrop-blur-xl rounded-xl sm:rounded-2xl flex items-center justify-center border border-white/10 mb-4 sm:mb-6 group-hover:scale-110 transition-transform">
                <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-blue-200" />
              </div>
              <h4 className="text-blue-200 font-black uppercase tracking-[0.2em] sm:tracking-[0.3em] text-[9px] sm:text-[10px] mb-2">Total Revenue Flow</h4>
              <h2 className="text-3xl sm:text-4xl lg:text-3xl xl:text-4xl 2xl:text-5xl font-black tracking-tighter mb-8 sm:mb-10 whitespace-nowrap overflow-hidden text-ellipsis">₹{(financials.totalRevenue || 0).toLocaleString()}</h2>

              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-4 bg-white/5 rounded-2xl sm:rounded-3xl border border-white/10">
                  <span className="text-[8px] sm:text-[10px] font-black uppercase tracking-widest text-blue-300">Disbursements</span>
                  <span className="text-lg sm:text-xl font-black">₹{(financials.totalPayouts || 0).toLocaleString()}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-4 bg-emerald-500/20 rounded-2xl sm:rounded-3xl border border-emerald-500/10">
                  <span className="text-[8px] sm:text-[10px] font-black uppercase tracking-widest text-emerald-400">Net Profit</span>
                  <span className="text-lg sm:text-xl font-black text-emerald-400">₹{(financials.netProfit || 0).toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 🧬 DETAILED FLOWS */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8">
        {/* Progress Tracker */}
        <div className="lg:col-span-5 xl:col-span-5 bg-white rounded-[24px] sm:rounded-[36px] p-5 sm:p-8 xl:p-10 shadow-2xl shadow-gray-200/50 border border-gray-50 flex flex-col items-center">
          <h3 className="text-lg sm:text-2xl font-black text-gray-900 self-start mb-6 sm:mb-8 tracking-tight">System Conversion</h3>
          <div className="relative w-full h-72">
            <ResponsiveContainer width="100%" height={288} minWidth={1} minHeight={1}>
              <PieChart>
                <Pie
                  data={gaugeData}
                  cx="50%"
                  cy="100%"
                  startAngle={180}
                  endAngle={0}
                  innerRadius={90}
                  outerRadius={125}
                  paddingAngle={0}
                  dataKey="value"
                  stroke="none"
                >
                  {gaugeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-end pb-12">
              <span className="text-7xl font-black text-[#1A1C1E] tabular-nums leading-none mb-2">{Math.round(gaugeData[0].value)}%</span>
              <p className="text-xs font-black text-gray-400 uppercase tracking-[0.4em]">Efficiency Rate</p>

            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 sm:gap-10 mt-6 sm:mt-12 w-full px-6">
            <FlowMetric label="Closed" value={leadStats.completed || 0} color="bg-emerald-500" />
            <FlowMetric label="Running" value={surgeryStats.in_progress || 0} color="bg-[#143E78]" />
            <FlowMetric label="New" value={leadStats.new || 0} color="bg-gray-200" />
          </div>
        </div>

        {/* Activity Stream */}
        <div className="lg:col-span-7 xl:col-span-7 bg-white rounded-[24px] sm:rounded-[36px] p-5 sm:p-8 xl:p-10 shadow-2xl shadow-gray-200/50 border border-gray-50">
          <div className="flex justify-between items-center mb-6 sm:mb-8 md:mb-12">
            <div>
              <h3 className="text-lg sm:text-2xl font-black text-gray-900 tracking-tight">Real-time Activity</h3>
              <p className="text-gray-400 font-bold uppercase text-[8px] sm:text-[10px] tracking-[0.2em] sm:tracking-[0.3em] mt-1 sm:mt-2">Latest patient updates and movements</p>
            </div>
            <Link to="/leads" className="p-3 sm:p-4 bg-gray-50 rounded-xl sm:rounded-2xl hover:bg-gray-100 transition-colors group shrink-0" title="View All Leads">
              <ArrowRightCircle className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400 group-hover:text-[#143E78] transition-colors" />
            </Link>
          </div>

          <div className="space-y-3 sm:space-y-6">
            {recentLeads.slice(0, 5).map((lead, idx) => (
              <ActivityRow key={lead._id} lead={lead} delay={idx * 100} />
            ))}
          </div>
        </div>

      </div>


      {/* 👥 AGENT WORKLOADS & ONLINE USERS */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8">

        {/* Agent Workloads Card */}
        <div className="lg:col-span-8 bg-white rounded-[24px] sm:rounded-[36px] p-5 sm:p-8 shadow-2xl shadow-gray-200/50 border border-gray-50 flex flex-col">
          <div className="mb-6 flex justify-between items-center">
            <div>
              <h3 className="text-lg sm:text-xl font-black text-slate-900 tracking-tight">Customer Care Workloads</h3>
              <p className="text-gray-400 font-bold uppercase text-[9px] sm:text-[10px] tracking-widest mt-1">Pending and processed lead counts per agent</p>
            </div>
          </div>

          <div className="overflow-x-auto w-full">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="pb-3 text-[10px] font-black text-gray-400 uppercase tracking-widest">Agent Name</th>
                  <th className="pb-3 text-[10px] font-black text-gray-400 uppercase tracking-widest">Contact Info</th>
                  <th className="pb-3 text-center text-[10px] font-black text-gray-400 uppercase tracking-widest">Pending Leads</th>
                  <th className="pb-3 text-center text-[10px] font-black text-gray-400 uppercase tracking-widest">Processed Today</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {agentStats.length > 0 ? (
                  agentStats.map(agent => (
                    <tr key={agent._id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="py-4 text-sm font-black text-slate-900">{agent.firstName} {agent.lastName}</td>
                      <td className="py-4 text-xs font-semibold text-slate-500">
                        <div>{agent.email}</div>
                        <div className="text-slate-400">{agent.phone || 'N/A'}</div>
                      </td>
                      <td className="py-4 text-center">
                        <span className="inline-flex items-center justify-center px-3 py-1 bg-amber-50 text-amber-700 text-xs font-black rounded-full border border-amber-100">
                          {agent.pendingLeadsCount}
                        </span>
                      </td>
                      <td className="py-4 text-center">
                        <span className="inline-flex items-center justify-center px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-black rounded-full border border-emerald-100">
                          {agent.processedLeadsCount}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="py-6 text-center text-xs font-bold text-gray-400 uppercase tracking-widest">No Active CC Agents found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Live Online Users Card */}
        <div className="lg:col-span-4 bg-white rounded-[24px] sm:rounded-[36px] p-5 sm:p-8 shadow-2xl shadow-gray-200/50 border border-gray-50 flex flex-col">
          <div className="mb-6 flex justify-between items-center">
            <div>
              <h3 className="text-lg sm:text-xl font-black text-slate-900 tracking-tight">Active Online Team</h3>
              <p className="text-gray-400 font-bold uppercase text-[9px] sm:text-[10px] tracking-widest mt-1">Real-time sessions</p>
            </div>
            <span className="flex h-3 w-3 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
            </span>
          </div>

          <div className="space-y-4 max-h-[380px] overflow-y-auto pr-1">
            {onlineUsers.length > 0 ? (
              onlineUsers.map(onlineUser => {
                const connectedSince = new Date(onlineUser.connectedAt);
                const diffMs = new Date() - connectedSince;
                const diffMins = Math.floor(diffMs / 60000);
                const diffSecs = Math.floor((diffMs % 60000) / 1000);
                const durationText = diffMins < 1
                  ? `${diffSecs} sec${diffSecs !== 1 ? 's' : ''}`
                  : diffMins < 60
                    ? `${diffMins} min${diffMins !== 1 ? 's' : ''}`
                    : `${Math.floor(diffMins / 60)} hr ${diffMins % 60} mins`;

                return (
                  <div key={onlineUser.userId} className="p-3 bg-slate-50 border border-slate-100 rounded-2xl flex justify-between items-center">
                    <div className="min-w-0">
                      <p className="text-xs font-black text-slate-900 truncate">
                        {onlineUser.firstName} {onlineUser.lastName}
                      </p>
                      <p className="text-[9px] text-slate-400 font-black uppercase tracking-wider mt-0.5">
                        {onlineUser.role.replace(/_/g, ' ')}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="text-[10px] font-black text-emerald-600 block">
                        Online
                      </span>
                      <span className="text-[8px] font-bold text-slate-400 block mt-0.5">
                        For {durationText}
                      </span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="py-12 text-center">
                <Users className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                <p className="text-xs font-bold text-slate-400">No active team members online</p>
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

// --- SUB-COMPONENTS (Simplified & Enhanced) ---

const QuickAction = ({ icon: Icon, label, color, onClick }) => (
  <button
    onClick={onClick}
    className="flex flex-col items-center justify-center p-3 sm:p-6 gap-2 sm:gap-3 hover:bg-gray-50 transition-all group rounded-2xl sm:rounded-3xl"
  >
    <div className={`w-10 h-10 sm:w-14 sm:h-14 ${color} rounded-xl sm:rounded-2xl flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform group-hover:shadow-md`}>
      <Icon className="w-5 h-5 sm:w-7 sm:h-7" />
    </div>
    <span className="text-[8px] sm:text-[10px] font-black text-gray-500 uppercase tracking-widest text-center">{label}</span>
  </button>
);

const MetricCard = ({ label, value, subLabel, icon: Icon, color, trend }) => {
  const colorMap = {
    indigo: 'bg-indigo-50 text-indigo-600 border-indigo-100',
    emerald: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    blue: 'bg-blue-50 text-blue-600 border-blue-100',
    amber: 'bg-amber-50 text-amber-600 border-amber-100'
  };

  return (
    <div className="bg-white p-5 sm:p-6 lg:p-8 rounded-[24px] sm:rounded-[32px] border border-gray-50 shadow-xl shadow-gray-200/30 group hover:shadow-2xl hover:-translate-y-1 transition-all">
      <div className="flex justify-between items-start mb-4 sm:mb-6">
        <div className={`w-10 h-10 sm:w-12 sm:h-12 ${colorMap[color].split(' ')[0]} rounded-xl flex items-center justify-center ${colorMap[color].split(' ')[1]} group-hover:rotate-12 transition-transform`}>
          <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
        </div>
        {trend && (
          <span className={`px-2 py-1 rounded-lg text-[8px] sm:text-[9px] font-black uppercase tracking-widest border ${colorMap[color].split(' ')[2]}`}>
            {trend}
          </span>
        )}
      </div>
      <h4 className="text-gray-400 text-[9px] sm:text-[10px] font-black uppercase tracking-widest mb-1">{label}</h4>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl sm:text-3xl lg:text-4xl font-black text-[#1A1C1E] tracking-tighter leading-none">{value || 0}</span>
      </div>
      <p className="text-[8px] sm:text-[9px] text-gray-400 font-bold mt-2 sm:mt-3 tracking-tight">{subLabel}</p>
    </div>
  );
};

const ActivityRow = ({ lead, delay }) => (
  <Link
    to={`/leads/${lead._id}`}
    className="flex items-center justify-between p-3 sm:p-5 hover:bg-gray-50 rounded-2xl sm:rounded-[32px] transition-all border border-transparent hover:border-gray-100 group animate-in fade-in slide-in-from-right-4 duration-500 gap-3 cursor-pointer"
    style={{ animationDelay: `${delay}ms` }}
  >
    <div className="flex items-center gap-3 sm:gap-6 min-w-0 flex-1">
      <div className="w-10 h-10 sm:w-16 sm:h-16 bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-50 flex items-center justify-center text-[#143E78] group-hover:scale-110 transition-transform flex-shrink-0">
        <Users className="w-5 h-5 sm:w-8 sm:h-8" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm sm:text-lg font-black text-gray-900 tracking-tight truncate group-hover:text-[#143E78] transition-colors">{lead.patientName}</p>
        <div className="flex flex-wrap items-center gap-1.5 sm:gap-3 mt-0.5 sm:mt-1">
          <span className="text-[8px] sm:text-[10px] font-black text-gray-400 uppercase tracking-widest">{new Date(lead.createdAt).toLocaleDateString()}</span>
          <div className="w-1 h-1 bg-gray-200 rounded-full"></div>
          <span className="text-[8px] sm:text-[9px] font-bold text-gray-400 uppercase tracking-widest sm:hidden">By: {lead.createdBy?.firstName || 'System'}</span>
          <div className="hidden sm:block w-1 h-1 bg-gray-200 rounded-full"></div>
          <span className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[8px] sm:text-[9px] font-black uppercase tracking-widest ${lead.status === 'confirmed' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
            }`}>{lead.status}</span>
        </div>
      </div>
    </div>
    <div className="flex items-center gap-3 flex-shrink-0">
      <span className="hidden sm:block text-[8px] sm:text-[9px] font-black text-gray-300 uppercase tracking-widest">Added by: {lead.createdBy?.firstName || 'System'}</span>
      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 rounded-lg sm:rounded-xl flex items-center justify-center opacity-100 sm:opacity-0 group-hover:opacity-100 group-hover:bg-[#143E78] group-hover:text-white transition-all">
        <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 group-hover:text-white" />
      </div>
    </div>
  </Link>
);

const FlowMetric = ({ label, value, color }) => (
  <div className="flex flex-col items-center gap-3">
    <div className={`w-3 h-3 rounded-full ${color} shadow-lg shadow-${color}/20`}></div>
    <div className="text-center">
      <p className="text-2xl font-black text-gray-900 leading-none mb-1">{value}</p>
      <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{label}</p>
    </div>
  </div>
);

const AdvancedTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-gray-900 text-white p-6 rounded-[24px] shadow-2xl border border-white/10 animate-in zoom-in duration-200">
        <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-3">{payload[0].payload.name}</p>
        <div className="flex items-baseline gap-3">
          <span className="text-4xl font-black">{payload[0].value}</span>
          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Active Leads</span>
        </div>
        <div className="mt-4 pt-4 border-t border-white/10">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
            <p className="text-[9px] font-bold text-gray-400">STATUS: VERIFIED</p>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

export default AdminDashboard;